/**
 * VersionComparisonPickers - Dual version picker for comparison views
 *
 * Uses the shared VersionCombobox component from @loader/ui.
 */

import React from 'react';
import { VersionCombobox, type VersionOption } from '@loader/ui';

import { SHABadge } from '@/components/common';

interface VersionComparisonPickersProps {
  selectedA: VersionOption | null;
  selectedB: VersionOption | null;
  onSelectA: (version: VersionOption | null) => void;
  onSelectB: (version: VersionOption | null) => void;
  versions: VersionOption[];
  showVersionA?: boolean;
  showVersionB?: boolean;
}

interface VersionDisplayProps {
  version: VersionOption | null;
  versions: VersionOption[];
}

function VersionDisplay({ version, versions }: VersionDisplayProps) {
  if (!version) {
    return (
      <span className="text-muted-foreground text-sm">No version selected</span>
    );
  }

  // Check if this version is "last created" (most recent without system tag)
  const hasAnySystemTag = versions.some(
    (v) => v.system_tag !== null && v.system_tag !== undefined
  );
  const isLastCreated = !hasAnySystemTag && versions[0]?.id === version.id;

  return (
    <div className="flex items-center gap-2">
      <SHABadge id={version.id} />
      <span className="text-sm">{version.file_name}</span>
      {isLastCreated && (
        <span className="text-muted-foreground text-xs">(last created)</span>
      )}
    </div>
  );
}

export const VersionComparisonPickers: React.FC<VersionComparisonPickersProps> = ({
  selectedA,
  selectedB,
  onSelectA,
  onSelectB,
  versions,
  showVersionA = true,
  showVersionB = false,
}) => {
  if (!showVersionA && !showVersionB) {
    return null;
  }

  const handleSelectA = (value: string) => {
    const version = versions.find((v) => v.id === value) || null;
    onSelectA(version);
  };

  const handleSelectB = (value: string) => {
    const version = versions.find((v) => v.id === value) || null;
    onSelectB(version);
  };

  return (
    <div
      className={`grid grid-cols-1 gap-4 ${showVersionB ? 'md:grid-cols-2' : 'md:w-1/2'}`}
    >
      {/* Version A */}
      {showVersionA && (
        <div className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">Version A</div>
          <div className="mb-3">
            <VersionDisplay version={selectedA} versions={versions} />
          </div>
          <VersionCombobox
            versions={versions}
            value={selectedA?.id || ''}
            onChange={handleSelectA}
            showSystemTagShortcuts={false}
            placeholder="Select version..."
          />
        </div>
      )}

      {/* Version B */}
      {showVersionB && (
        <div className="rounded-lg border p-3">
          <div className="mb-2 text-sm font-medium">Version B</div>
          <div className="mb-3">
            <VersionDisplay version={selectedB} versions={versions} />
          </div>
          <VersionCombobox
            versions={versions}
            value={selectedB?.id || ''}
            onChange={handleSelectB}
            showSystemTagShortcuts={false}
            placeholder="Select version..."
          />
        </div>
      )}
    </div>
  );
};
